import { useEffect, useState } from "react";
import { Award, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import {
  TabKicker,
  TabSectionTitle,
  ChartCard,
  DataListCard,
  DataRow,
} from "./_shell";
import NssRadarChart from "@/components/app/NssRadarChart";
import StudentExperienceRadar from "@/components/app/StudentExperienceRadar";
import QiltFocusAreaDeepDive from "@/components/app/QiltFocusAreaDeepDive";

/**
 * TabTeaching — editorial 2-column bento.
 *   • Left (col-span 7): NSS radar with sharp polygon styling
 *   • Right (col-span 5): TEF chip + destination bar + support stats
 *
 * Testids preserved: tab-teaching, nss-radar, tef-chip, destination-bar
 */

const NSS_LABELS = {
  teaching:      "Teaching",
  opportunities: "Opportunities",
  feedback:      "Feedback",
  support:       "Academic support",
  organisation:  "Organisation",
};

function NssRadar({ nss }) {
  const size = 340;
  const cx = size / 2, cy = size / 2;
  const r = 78;
  const axes = Object.keys(NSS_LABELS);
  const angle = (i) => (i * 2 * Math.PI) / axes.length - Math.PI / 2;
  const point = (i, v) => {
    const rr = (r * v) / 100;
    return [cx + rr * Math.cos(angle(i)), cy + rr * Math.sin(angle(i))];
  };
  const gridPoints = (v) => axes.map((_, i) => point(i, v).join(",")).join(" ");
  const dataPoints = axes.map((k, i) => point(i, nss[k] || 0).join(",")).join(" ");
  return (
    <svg width="100%" height={size} viewBox={`0 0 ${size} ${size}`}
         preserveAspectRatio="xMidYMid meet"
         className="max-w-full h-auto" data-testid="nss-radar">
      {[25, 50, 75, 100].map((v) => (
        <polygon key={v} points={gridPoints(v)}
                 fill="none"
                 className="stroke-[#002147]/10"
                 strokeWidth="1"
                 strokeDasharray={v === 100 ? "0" : "3 2"} />
      ))}
      {axes.map((_, i) => {
        const [x, y] = point(i, 100);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y}
                     className="stroke-[#002147]/10" strokeWidth="1" />;
      })}
      <polygon points={dataPoints}
               className="fill-[#7C3AED]/10 stroke-[#7C3AED]"
               strokeWidth="2" />
      {axes.map((k, i) => {
        const [x, y] = point(i, 100);
        // Push labels well outside the polygon so long words like
        // "ACADEMIC SUPPORT" and "OPPORTUNITIES" don't clip the SVG
        // edge. Distance is proportional to `r` so any future radius
        // tweak flows through.
        const [tx, ty] = [
          cx + (r + 32) * Math.cos(angle(i)),
          cy + (r + 32) * Math.sin(angle(i)),
        ];
        return (
          <g key={k}>
            <circle cx={x} cy={y} r={3} className="fill-[#7C3AED]" />
            <text x={tx} y={ty} textAnchor="middle"
                  className="font-mono fill-[#002147]/60"
                  style={{ fontSize: 9, letterSpacing: 1 }}>
              {NSS_LABELS[k].toUpperCase()}
            </text>
            <text x={tx} y={ty + 13} textAnchor="middle"
                  className="font-mono fill-[#002147]"
                  style={{ fontSize: 12, fontWeight: 600 }}>
              {nss[k]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function DestinationBar({ dest }) {
  if (!dest) return null;
  const segs = [
    { key: "work_pct",          label: "In work",       color: "#059669" },
    { key: "further_study_pct", label: "Further study", color: "#7C3AED" },
    { key: "other_pct",         label: "Other",         color: "#78716C" },
    { key: "unemployed_pct",    label: "Unemployed",    color: "#B91C1C" },
  ];
  const total = segs.reduce((s, x) => s + (dest[x.key] || 0), 0) || 100;
  return (
    <div data-testid="destination-bar">
      <div className="flex h-6 w-full border border-[#002147]/10">
        {segs.map((s) => {
          const pct = ((dest[s.key] || 0) / total) * 100;
          if (pct < 0.5) return null;
          return (
            <div
              key={s.key}
              className="flex items-center justify-center text-white font-mono font-semibold"
              style={{ width: `${pct}%`, backgroundColor: s.color, fontSize: 10 }}
              title={`${s.label} · ${dest[s.key]}%`}
            >
              {pct > 8 ? `${dest[s.key]}%` : ""}
            </div>
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-y-1 gap-x-4">
        {segs.map((s) => (
          <div key={s.key} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider">
            <span className="inline-block h-2 w-2" style={{ backgroundColor: s.color }} />
            <span className="text-[#002147]/60">{s.label}</span>
            <span className="text-[#002147] font-semibold ml-auto">{dest[s.key]}%</span>
          </div>
        ))}
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#002147]/50">
        HESA Graduate Outcomes · {dest.n_courses} courses · 15 mo post-grad
      </p>
    </div>
  );
}

function TefChip({ tef }) {
  if (!tef) return null;
  const style =
    tef.dominant === "Gold"
      ? "bg-gradient-to-r from-amber-200 to-amber-100 text-amber-900 border-amber-300"
      : tef.dominant === "Silver"
      ? "bg-gradient-to-r from-stone-200 to-stone-100 text-stone-900 border-stone-300"
      : tef.dominant === "Bronze"
      ? "bg-gradient-to-r from-orange-200 to-orange-100 text-orange-900 border-orange-300"
      : "bg-[#F3F1EC] text-[#002147]/70 border-[#002147]/20";
  return (
    <a
      href={tef.url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 border px-3 py-1 font-underground-sc text-xs font-bold tracking-widest hover:opacity-80 transition ${style}`}
      data-testid="tef-chip"
    >
      <Award className="h-3.5 w-3.5" />
      TEF · {tef.dominant}
      {tef.url && <ExternalLink className="h-3 w-3 opacity-70" />}
    </a>
  );
}

export default function TabTeaching({ uni }) {
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/universities/${uni.id}/teaching-signals`)
      .then((r) => { if (!cancelled) setSignals(r.data); })
      .catch(() => { if (!cancelled) setSignals(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [uni.id]);

  const hasNss = signals?.teaching?.nss;
  const hasDest = signals?.destinations;

  return (
    <section className="space-y-8" data-testid="tab-teaching">
      <div>
        <TabKicker>01 · Teaching & Outcomes</TabKicker>
        <TabSectionTitle>How students rate the classroom, and where they land</TabSectionTitle>
      </div>

      <div className="space-y-6">
        {uni.country === "UK" && <NssRadarChart uniId={uni.id} />}
        {(uni.country === "AU" || uni.country === "Australia" || uni.country === "US" || uni.country === "United States") && <StudentExperienceRadar uni={uni} />}
        {(uni.country === "AU" || uni.country === "Australia") && <QiltFocusAreaDeepDive uniId={uni.id} />}

        {hasNss && (
          <ChartCard
            title="Student satisfaction · NSS 2024"
            legend={<span>{signals.teaching.n_courses} courses · scale 0–100</span>}
            headerRight={<TefChip tef={signals.teaching.tef} />}
          >
            <div className="flex justify-center">
              <NssRadar nss={signals.teaching.nss} />
            </div>
            <p className="mt-4 font-sans text-sm text-[#002147]/70 leading-relaxed border-t border-[#002147]/10 pt-4">
              National Student Survey scores averaged across all this university's
              courses. Further from centre = stronger.
            </p>
          </ChartCard>
        )}

        {hasDest && (
          <ChartCard title="Where graduates are 15 months later">
            <DestinationBar dest={signals.destinations} />
          </ChartCard>
        )}
      </div>

      <div>
        <TabKicker>02 · Support Services</TabKicker>
        <TabSectionTitle>Beyond the lecture theatre</TabSectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DataListCard title="Retention & satisfaction">
            <DataRow label="Student-faculty ratio"
                     value={uni.student_faculty_ratio ? `${uni.student_faculty_ratio}:1` : "—"} />
            <DataRow label="1st-year retention"
                     value={uni.first_year_retention != null ? `${(uni.first_year_retention * 100).toFixed(0)}%` : "—"} />
            <DataRow label="Completion rate"
                     value={uni.completion_rate != null ? `${(uni.completion_rate * 100).toFixed(0)}%` : "—"} />
            <DataRow label="Satisfaction (uni-wide)"
                     value={uni.satisfaction_score != null ? `${uni.satisfaction_score}/5` : "—"}
                     sublabel={uni.satisfaction_source || undefined} />
          </DataListCard>
          <DataListCard title="Support access">
            <DataRow label="Academic advising" value={uni.academic_advising || "—"} />
            <DataRow label="Careers guidance" value={uni.careers_guidance || "—"} />
            <DataRow label="Mental health" value={uni.mental_health || "—"} />
            <DataRow label="Disability support" value={uni.disability_support || "—"} />
            <DataRow label="Tutoring / writing" value={uni.tutoring || "—"} />
          </DataListCard>
        </div>
      </div>

      {loading && !signals && (
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/40 text-center py-2">
          Loading uni-wide teaching signals…
        </div>
      )}
    </section>
  );
}
